import { extractMentionedDevices } from '#pplx/frontend/apps/ask/ask-input/query-serialization/lexicalNodeToQuery.js';
import { useEntropyBrowser } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { useTabsAttachments } from '#pplx/frontend/apps/ask/hooks/comet-hooks/useCometTabsAttachments.js';
import { useAtMentionDevices } from '#pplx/frontend/apps/ask/hooks/device-hooks/useAtMentionDevices.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useAtMentionSearchModes } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useAtMentionSearchModes.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useGlobalAvailableSources } from '#pplx/frontend/apps/ask/hooks/shared-source-selection-hooks/useSharedSourceSelection.js';
import { useAtMentionSpaces } from '#pplx/frontend/apps/ask/hooks/spaces-hooks/useAtMentionSpaces.js';
import { getMentionMenuInclusionPolicy } from '#pplx/frontend/apps/ask/mention-typeahead/getMentionMenuInclusionPolicy.js';
import { useMentionTabAttachments } from '#pplx/frontend/apps/ask/mention-typeahead/useMentionTabAttachments.js';
import { useSkillsTypeahead } from '#pplx/frontend/apps/ask/skills/hooks/useSkillsTypeahead.js';
import { useUserInputJson } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useCallback, useMemo } from 'react';
/**
 * Composes mention sections, selection actions, and tab attachment reconciliation independently of search text.
 * Source and user search hooks consume the returned inclusion gates and source-selection action separately.
 */
export function useMentionTypeaheadContext({ reason, isLazy = true, variants, omittedSearchModes, collectionName, collectionUuid, forceSkillsEnabled = false, omitDeviceMentions = false, useComputerCommandMenu = false, onComputerCommandMenuOpenChange, inputJson, }) {
    const cometBrowser = useEntropyBrowser();
    const selectedSearchMode = useSelectedSearchMode();
    const isComputerMode = selectedSearchMode === SearchMode.ASI;
    const shouldUseComputerCommandMenu = useComputerCommandMenu && isComputerMode;
    // The generic "/" menu is search-mode-only by default. Computer-native
    // callers set `forceSkillsEnabled` to keep skills live regardless of the
    // global thread search mode.
    const { includeTabs, includeSpaces, includeSources, includeSearchModes, includeSkills, includeDevices, includeUsers, } = getMentionMenuInclusionPolicy({
        variants,
        isComputerMode,
        forceSkillsEnabled,
    });
    const { tabs, tabAttachmentsEnabled } = useTabsAttachments(cometBrowser, includeTabs);
    const { isAtMentionSpacesEnabled, mentionSpacesOptions, onTrigger: onTriggerSpaces, } = useAtMentionSpaces({
        reason,
        collectionName,
        isLazy,
        enabled: includeSpaces,
    });
    const { isSkillsTypeaheadEnabled, skillsTypeaheadOptions, onTrigger: onTriggerSkills, } = useSkillsTypeahead({ enabled: includeSkills, collectionUuid });
    const openPaywallUpsell = usePaywallUpsell();
    const onSearchModeSelected = useCallback((mode) => {
        if (mode === SearchMode.ASI) {
            onComputerCommandMenuOpenChange?.(true);
        }
    }, [onComputerCommandMenuOpenChange]);
    const { isAtMentionSearchModesEnabled, mentionSearchModesOptions } = useAtMentionSearchModes({
        enabled: includeSearchModes,
        separateComputerMode: true,
        omittedSearchModes,
        onSearchModeSelected,
        openPaywallUpsell,
    });
    const { addSource } = useGlobalAvailableSources();
    const storeInputJson = useUserInputJson();
    const userInputJson = inputJson ?? storeInputJson;
    // A query routes to at most one device, matching the macOS/iOS pickers:
    // once a device mention is in the input, the Devices section disappears
    // from subsequent '@' menus until the mention is removed.
    const hasDeviceMention = useMemo(() => extractMentionedDevices(userInputJson?.root).length > 0, [userInputJson]);
    const { isAtMentionDevicesEnabled, mentionDevicesOptions, onTrigger: onTriggerDevices, } = useAtMentionDevices({
        reason,
        isLazy,
        enabled: includeDevices && !hasDeviceMention && !omitDeviceMentions,
    });
    useMentionTabAttachments({ userInputJson });
    const onMentionSource = useCallback((sourceId) => addSource(sourceId), [addSource]);
    return {
        isComputerMode,
        shouldUseComputerCommandMenu,
        includeSources,
        includeUsers,
        tabOptions: tabs,
        tabsEnabled: tabAttachmentsEnabled,
        spacesEnabled: isAtMentionSpacesEnabled,
        spaceOptions: mentionSpacesOptions,
        onTriggerSpaces,
        skillsEnabled: isSkillsTypeaheadEnabled,
        skillOptions: skillsTypeaheadOptions,
        onTriggerSkills,
        searchModesEnabled: isAtMentionSearchModesEnabled,
        searchModeOptions: mentionSearchModesOptions,
        devicesEnabled: isAtMentionDevicesEnabled,
        deviceOptions: mentionDevicesOptions,
        onTriggerDevices,
        onMentionSource,
    };
}
//# sourceMappingURL=useMentionTypeaheadContext.js.map